import React, { useState, useEffect } from 'react';
import { Plus, FolderOpen, Calendar, Users, ChevronDown, ChevronUp } from 'lucide-react';
import { supabase } from '../supabaseClient';
import { toast } from 'sonner';
import LogCard from '../components/LogCard';
import LogFormModal from '../components/LogFormModal';

const Logger = () => {
  const [logs, setLogs] = useState([]);
  const [userProfile, setUserProfile] = useState(null);
  // Advanced filtering state
  const [viewMode, setViewMode] = useState('specific_date'); // 'specific_date' or 'all_history'
  const [selectedDate, setSelectedDate] = useState(() => {
    // Default to today in YYYY-MM-DD format
    const today = new Date();
    return today.toISOString().split('T')[0];
  });
  const [selectedUserId, setSelectedUserId] = useState('ALL');
  const [usersList, setUsersList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showFormModal, setShowFormModal] = useState(false);
  const [editingLog, setEditingLog] = useState(null);
  const [viewingLog, setViewingLog] = useState(null);
  const [isViewMode, setIsViewMode] = useState(false);
  const [currentUserId, setCurrentUserId] = useState(null);
  const [showDriveLinksDropdown, setShowDriveLinksDropdown] = useState(false);

  useEffect(() => {
    loadCurrentUser();
  }, []);

  useEffect(() => {
    if (currentUserId) {
      loadLogs();
    }
  }, [selectedDate, currentUserId, viewMode, selectedUserId]);

  useEffect(() => {
    if (currentUserId) {
      loadUserProfile();
    }
  }, [currentUserId]);

  useEffect(() => {
    // Fetch all users if current user is admin
    if (currentUserId && userProfile?.role === 'admin') {
      loadAllUsers();
    }
  }, [currentUserId, userProfile?.role]);

  const loadCurrentUser = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (user) {
        setCurrentUserId(user.id);
      }
    } catch (error) {
      console.error('Error loading current user:', error);
      toast.error('Failed to load user information');
    }
  };

  const loadUserProfile = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        setUserProfile(null);
        return;
      }

      // Fetch current user's profile from public.users table
      const { data, error } = await supabase
        .from('users')
        .select('*')
        .eq('id', user.id)
        .single();

      if (error) {
        console.error('Error loading user profile:', error);
        setUserProfile(null);
        return;
      }

      setUserProfile(data || null);
    } catch (error) {
      console.error('Error loading user profile:', error);
      setUserProfile(null);
    }
  };

  const loadAllUsers = async () => {
    try {
      // Fetch all users for admin dropdown
      const { data, error } = await supabase
        .from('users')
        .select('id, name, email, is_active, inactive_user_name, drive_folder_link')
        .order('name', { ascending: true });

      if (error) {
        console.error('Error loading users list:', error);
        toast.error('Failed to load users list');
        return;
      }

      setUsersList(data || []);
    } catch (error) {
      console.error('Error loading users list:', error);
      toast.error('Failed to load users list');
    }
  };

  const loadLogs = async () => {
    try {
      setLoading(true);
      
      // Base query with join to users table
      let query = supabase
        .from('daily_logs')
        .select('*, users(name)')
        .order('created_at', { ascending: false });

      // Apply date filter only if viewMode is 'specific_date'
      if (viewMode === 'specific_date') {
        query = query.eq('log_date', selectedDate);
      }

      // Apply user filter if selectedUserId is set and not 'ALL'
      if (selectedUserId && selectedUserId !== 'ALL') {
        query = query.eq('user_id', selectedUserId);
      }

      const { data, error } = await query;

      if (error) {
        console.error('Error loading logs:', error);
        toast.error('Failed to load logs: ' + error.message);
        return;
      }

      setLogs(data || []);
    } catch (error) {
      console.error('Error loading logs:', error);
      toast.error('Failed to load logs: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateLog = () => {
    setEditingLog(null);
    setViewingLog(null);
    setIsViewMode(false);
    setShowFormModal(true);
  };

  const handleEditLog = (log) => {
    setEditingLog(log);
    setViewingLog(null);
    setIsViewMode(false);
    setShowFormModal(true);
  };

  const handleViewLog = (log) => {
    setViewingLog(log);
    setEditingLog(null);
    setIsViewMode(true);
    setShowFormModal(true);
  };

  const handleSaveLog = async (logData) => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        toast.error('You must be logged in to save logs');
        return;
      }

      if (editingLog) {
        // Update existing log
        const { data, error } = await supabase
          .from('daily_logs')
          .update({
            focus: logData.todays_focus,
            work_done: logData.work_done,
            blockers: logData.blockers,
            next_steps: logData.next_steps
          })
          .eq('id', editingLog.id)
          .select('id, log_date, focus, work_done, blockers, next_steps, user_id, users(name)');

        if (error) throw error;

        toast.success('Log updated successfully!');
      } else {
        // Create new log - check for duplicates first
        const logDate = logData.date || selectedDate;
        
        // Check if a log already exists for this user and date
        const { data: existingLog, error: checkError } = await supabase
          .from('daily_logs')
          .select('id')
          .eq('user_id', user.id)
          .eq('log_date', logDate)
          .maybeSingle();

        if (checkError && checkError.code !== 'PGRST116') {
          // PGRST116 is "no rows returned" - that's fine
          throw checkError;
        }

        if (existingLog) {
          toast.error('You have already submitted a log for this date. Please edit the existing one instead.');
          return; // Don't close modal, let user fix the date
        }

        // Insert new log
        const { data, error } = await supabase
          .from('daily_logs')
          .insert([{
            user_id: user.id,
            log_date: logDate,
            focus: logData.todays_focus,
            work_done: logData.work_done,
            blockers: logData.blockers,
            next_steps: logData.next_steps
          }])
          .select('id, log_date, focus, work_done, blockers, next_steps, user_id, users(name)');

        if (error) {
          // Check for unique constraint violation (Postgres error code 23505)
          if (error.code === '23505') {
            toast.error('You have already submitted a log for this date. Please edit the existing one instead.');
            return; // Don't close modal, let user fix the date
          }
          throw error;
        }

        toast.success('Log created successfully!');
      }

      setShowFormModal(false);
      setEditingLog(null);
      setViewingLog(null);
      setIsViewMode(false);
      await loadLogs();
    } catch (error) {
      console.error('Error saving log:', error);
      
      // Check for unique constraint violation (Postgres error code 23505)
      if (error.code === '23505') {
        toast.error('You have already submitted a log for this date. Please edit the existing one instead.');
        // Don't close modal on duplicate error
        return;
      }
      
      toast.error('Failed to save log: ' + error.message);
      // Don't close modal on other errors either, so user can fix issues
    }
  };

  const handleOpenMyDrive = () => {
    const driveLink = userProfile?.drive_folder_link;
    if (driveLink && driveLink.trim() !== '') {
      window.open(driveLink, '_blank');
    } else {
      toast.error('Drive folder link not found');
    }
  };

  const formatDateDisplay = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      weekday: 'long', 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });
  };

  if (loading && logs.length === 0) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-center">
          <div className="loading-dots mb-4">
            <span></span>
            <span></span>
            <span></span>
          </div>
          <p className="text-gray-400">Loading logs...</p>
        </div>
      </div>
    );
  }

  const isAdmin = userProfile?.role === 'admin';

  return (
    <div className="p-8" data-testid="logger-page">
      {/* Filter Bar */}
      <div className="glass-card p-6 mb-6">
        <div className="flex flex-col gap-4">
          {/* View Mode Toggle */}
          <div className="flex items-center gap-4">
            <span className="text-sm font-medium text-gray-300">View Mode:</span>
            <div className="flex gap-2">
              <button
                onClick={() => setViewMode('specific_date')}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  viewMode === 'specific_date'
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-700/50 text-gray-400 hover:bg-gray-700'
                }`}
                data-testid="view-mode-specific-date"
              >
                Specific Date
              </button>
              <button
                onClick={() => setViewMode('all_history')}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  viewMode === 'all_history'
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-700/50 text-gray-400 hover:bg-gray-700'
                }`}
                data-testid="view-mode-all-history"
              >
                All History
              </button>
            </div>
          </div>

          {/* Filters Row */}
          <div className="flex flex-col md:flex-row md:items-center gap-4">
            {/* Date Picker - Only show if viewMode is 'specific_date' */}
            {viewMode === 'specific_date' && (
              <div className="flex items-center gap-4">
                <div className="flex items-center gap-2">
                  <Calendar className="text-gray-400" size={20} />
                  <input
                    type="date"
                    value={selectedDate}
                    onChange={(e) => setSelectedDate(e.target.value)}
                    className="glass-input px-4 py-2 rounded-lg"
                    data-testid="date-picker"
                  />
                </div>
                <div className="text-gray-400 text-sm">
                  {formatDateDisplay(selectedDate)}
                </div>
              </div>
            )}

            {/* User Dropdown - Admin Only */}
            {isAdmin && (
              <div className="flex items-center gap-2">
                <Users className="text-gray-400" size={20} />
                <select
                  value={selectedUserId}
                  onChange={(e) => setSelectedUserId(e.target.value)}
                  className="glass-input px-4 py-2 rounded-lg min-w-[200px] text-white"
                  data-testid="user-filter-dropdown"
                >
                  <option value="ALL" className="bg-gray-800 text-white">All Users</option>
                  {usersList.map((user) => {
                    const isInactive = user.is_active === false;
                    const realName = user.name || user.email;
                    const displayName = isInactive && user.inactive_user_name 
                      ? user.inactive_user_name 
                      : realName;
                    return (
                      <option 
                        key={user.id} 
                        value={user.id}
                        className={isInactive ? 'bg-gray-800 text-gray-400' : 'bg-gray-800 text-white'}
                      >
                        {displayName}
                      </option>
                    );
                  })}
                </select>
              </div>
            )}

            {/* Admin Drive Links Dropdown */}
            {isAdmin && usersList.filter(user => user.drive_folder_link && user.drive_folder_link.trim() !== '').length > 0 && (
              <div className="flex items-center gap-2">
                <FolderOpen className="text-gray-400" size={20} />
                <select
                  onChange={(e) => {
                    if (e.target.value) {
                      window.open(e.target.value, '_blank');
                      e.target.value = ''; // Reset dropdown
                    }
                  }}
                  className="glass-input px-4 py-2 rounded-lg min-w-[200px] text-white"
                  defaultValue=""
                >
                  <option value="" className="bg-gray-800 text-white">Open User Drive...</option>
                  {usersList
                    .filter(user => user.drive_folder_link && user.drive_folder_link.trim() !== '')
                    .map((user) => {
                      const isInactive = user.is_active === false;
                      const realName = user.name || user.email;
                      const displayName = isInactive && user.inactive_user_name 
                        ? user.inactive_user_name 
                        : realName;
                      return (
                        <option 
                          key={user.id} 
                          value={user.drive_folder_link}
                          className="bg-gray-800 text-white"
                        >
                          {displayName}
                        </option>
                      );
                    })}
                </select>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Header with Action Buttons */}
      <div className="glass-card p-6 mb-6">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div className="text-gray-400 text-sm">
            {viewMode === 'all_history' 
              ? 'Showing all logs' + (selectedUserId !== 'ALL' && isAdmin ? ` for selected user` : '')
              : `Logs for ${formatDateDisplay(selectedDate)}`
            }
          </div>

          {/* Right side: Buttons */}
          <div className="flex items-center gap-3">
            {userProfile?.drive_folder_link && userProfile.drive_folder_link.trim() !== '' && (
              <button
                onClick={handleOpenMyDrive}
                className="btn-primary flex items-center gap-2 bg-green-600 hover:bg-green-500"
                data-testid="my-drive-button"
              >
                <FolderOpen size={18} />
                My Drive Folder
              </button>
            )}
            <button
              onClick={handleCreateLog}
              className="btn-primary flex items-center gap-2"
              data-testid="create-log-button"
            >
              <Plus size={18} />
              Create Log
            </button>
          </div>
        </div>
      </div>

      {/* Grid of Log Cards */}
      {logs.length === 0 ? (
        <div className="glass-card p-12 text-center">
          <p className="text-gray-400 text-lg mb-4">
            {viewMode === 'all_history' 
              ? 'No logs found' + (selectedUserId !== 'ALL' && isAdmin ? ' for selected user' : '')
              : `No logs found for ${formatDateDisplay(selectedDate)}`
            }
          </p>
          <button
            onClick={handleCreateLog}
            className="btn-primary"
          >
            Create First Log
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6">
          {logs.map((log) => (
            <LogCard
              key={log.id}
              log={log}
              onEdit={() => handleEditLog(log)}
              onView={() => handleViewLog(log)}
            />
          ))}
        </div>
      )}

      {/* Form Modal */}
      {showFormModal && (
        <LogFormModal
          log={isViewMode ? viewingLog : editingLog}
          selectedDate={selectedDate}
          currentUserId={currentUserId}
          userDriveLink={userProfile?.drive_folder_link}
          readOnly={isViewMode}
          onClose={() => {
            setShowFormModal(false);
            setEditingLog(null);
            setViewingLog(null);
            setIsViewMode(false);
          }}
          onSubmit={handleSaveLog}
        />
      )}
    </div>
  );
};

export default Logger;

